import { type FC, type JSX, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'

import { Checkbox, FormHelperText } from 'base-ui'

import { useGlobalConfigContext } from '../../../../global-config'
import { TENANT } from '../../../../tenant'
import { useMarketplacePartnerRegisterPageContext } from '../../marketplacePartnerRegisterPageContext'

const markets: {
  country: TENANT
  hiddenInTenant?: TENANT[]
  label: JSX.Element
}[] = [
  {
    country: TENANT.COM,
    label: (
      <FormattedMessage id="marketplacePartnerRegisterPage.basicInformations.countries.germany" />
    ),
  },
  {
    country: TENANT.AT,
    label: (
      <FormattedMessage id="marketplacePartnerRegisterPage.basicInformations.countries.austria" />
    ),
  },
  {
    country: TENANT.IT,
    label: (
      <FormattedMessage id="marketplacePartnerRegisterPage.basicInformations.countries.italy" />
    ),
  },
  {
    country: TENANT.BE,
    label: (
      <FormattedMessage id="marketplacePartnerRegisterPage.basicInformations.countries.belgium" />
    ),
  },
  {
    country: TENANT.FR,
    hiddenInTenant: [TENANT.COM, TENANT.AT, TENANT.BE],
    label: (
      <FormattedMessage id="marketplacePartnerRegisterPage.basicInformations.countries.france" />
    ),
  },
  {
    country: TENANT.CH,
    hiddenInTenant: [TENANT.COM, TENANT.AT, TENANT.BE],
    label: (
      <FormattedMessage id="marketplacePartnerRegisterPage.basicInformations.countries.switzerland" />
    ),
  },
]

export const MarketplacePartnerRegisterPageFormBasicInformationsMarkets: FC =
  () => {
    const { formState } = useMarketplacePartnerRegisterPageContext()
    formState.use()
    const { tenant } = useGlobalConfigContext()

    const { model } = formState
    const handleCheckboxChange = useCallback(
      (countryCode: TENANT, isChecked: boolean): void => {
        const updatedCountries = isChecked
          ? [...model.markets, countryCode]
          : model.markets.filter((code: TENANT) => code !== countryCode)
        formState.setValue(updatedCountries, model, 'markets')
      },
      [formState, model],
    )

    return (
      <div className="flex flex-col gap-4">
        <div>
          <div className="text-l">
            <FormattedMessage id="marketplacePartnerRegisterPage.basicInformations.countries.title" />
          </div>
          <FormattedMessage id="marketplacePartnerRegisterPage.basicInformations.countries.description" />
        </div>
        <div className="flex flex-col gap-5 tablet:flex-row">
          {markets
            .filter((market) => !market.hiddenInTenant?.includes(tenant))
            .map((market) => (
              <label className="flex items-center gap-2" key={market.country}>
                <Checkbox
                  checked={model.markets.includes(market.country)}
                  data-qa-id={`sellerForm-checkbox-country-${market.country}`}
                  onChange={(event): void => {
                    handleCheckboxChange(market.country, event.target.checked)
                  }}
                />
                <span>{market.label}</span>
              </label>
            ))}
        </div>
        {model.validator.getFirstErrorFor('markets') && (
          <FormHelperText
            data-qa-id="global-MessageForm-countries-error-msg"
            error
          >
            {model.validator.getFirstErrorFor('markets')}
          </FormHelperText>
        )}
      </div>
    )
  }
